import React from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';
import { useStyles } from '../styles/PracticasConvenio.styles';
import BadgeEstado from './BadgeEstado';
import BadgeVencimiento from './BadgeVencimiento';
import FilaAcciones from './FilaAcciones';
import { formatFechaCorta, formatEstudiante, formatTutor, truncateText } from '../utils/formatters';

const TablaPracticas = ({
  rows,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onVerDetalle,
  onVerConvenio,
  loading,
  consultaRealizada,
}) => {
  const classes = useStyles();

  if (!consultaRealizada && !loading) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Use los filtros para consultar
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Puede buscar sin filtros o combinar periodo, C.I., estados, institucion, carrera y servicio.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box className={classes.emptyState}>
        <CircularProgress size={48} />
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          Cargando practicas...
        </Typography>
      </Box>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No hay practicas
        </Typography>
        <Typography variant="body2" color="textSecondary">
          No se encontraron practicas con los filtros seleccionados.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper className={classes.tableContainer}>
      <TableContainer className={classes.tableScroll}>
        <Table className={classes.table} size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="center" className={`${classes.accionesColumn} ${classes.stickyAcciones} ${classes.stickyAccionesHeader}`}>Acciones</TableCell>
              <TableCell className={`${classes.estadoColumn} ${classes.stickyEstado} ${classes.stickyEstadoHeader}`}>Estado</TableCell>
              <TableCell className={`${classes.alumnoColumn} ${classes.stickyAlumno} ${classes.stickyAlumnoHeader}`}>Alumno</TableCell>
              <TableCell className={classes.institucionColumn}>Institucion</TableCell>
              <TableCell className={classes.servicioColumn}>Servicio</TableCell>
              <TableCell className={classes.fechasColumn}>Periodo</TableCell>
              <TableCell align="center" className={classes.horasColumn}>Horas</TableCell>
              <TableCell className={classes.tutorColumn}>Tutor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                const estudiante = formatEstudiante(row.nombreApellido, row.cedulaIdentidad);
                const tutor = formatTutor(row.tutor, row.ciTutor);

                return (
                  <TableRow key={`${row.cronogramaId}-${row.cedulaIdentidad}-${index}`} className={classes.tableRow} hover>
                    <TableCell align="center" className={`${classes.accionesColumn} ${classes.stickyAcciones}`}>
                      <FilaAcciones
                        row={row}
                        onVerDetalle={onVerDetalle}
                        onVerConvenio={onVerConvenio}
                      />
                    </TableCell>

                    <TableCell className={`${classes.estadoColumn} ${classes.stickyEstado}`}>
                      <BadgeEstado estado={row.practicaEstado} />
                    </TableCell>

                    <TableCell className={`${classes.alumnoColumn} ${classes.stickyAlumno}`}>
                      <Tooltip title={estudiante.nombre} arrow>
                        <Box>
                          <Typography variant="body2" className={classes.primaryText}>
                            <PersonIcon className={classes.inlineIcon} />
                            {truncateText(estudiante.nombre, 34)}
                          </Typography>
                          <Typography variant="caption" className={classes.metaText}>
                            CI {estudiante.ci}
                          </Typography>
                          <Typography variant="caption" className={classes.metaText} display="block">
                            Cronograma #{row.cronogramaId || '-'} &bull; Convenio #{row.convenioId || '-'}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell className={classes.institucionColumn}>
                      <Tooltip title={`${row.institucion || '-'} - ${row.carrera || '-'}`} arrow>
                        <Box>
                          <Typography variant="body2" className={classes.secondaryText}>
                            {truncateText(row.institucion, 28)}
                          </Typography>
                          <Typography variant="caption" className={classes.metaText}>
                            {truncateText(row.carrera, 28)}
                          </Typography>
                          <Typography variant="caption" className={classes.metaText} display="block">
                            Curso {row.curso || '-'} &bull; Sem. {row.semestre || '-'}
                          </Typography>
                          <BadgeVencimiento fechaFinVigencia={row.fechaFinVigencia} />
                        </Box>
                      </Tooltip>
                    </TableCell>

                    <TableCell className={classes.servicioColumn}>
                      <Tooltip title={row.nombreServicio || '-'} arrow>
                        <Typography variant="body2" className={classes.secondaryText}>
                          {truncateText(row.nombreServicio, 28)}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell className={classes.fechasColumn}>
                      <Typography variant="body2" className={classes.dateText}>
                        {formatFechaCorta(row.fechaDesde) || '-'}
                      </Typography>
                      <Typography variant="caption" className={classes.metaText} display="block">
                        a {formatFechaCorta(row.fechaHasta) || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell align="center" className={classes.horasColumn}>
                      <Box className={classes.hoursBox}>
                        <ScheduleIcon className={classes.inlineIcon} />
                        <Typography variant="body2">{row.totalHoras || 0} h</Typography>
                      </Box>
                    </TableCell>

                    <TableCell className={classes.tutorColumn}>
                      <Tooltip title={`${tutor.nombre} - C.I.: ${tutor.ci}`} arrow>
                        <Box>
                          <Typography variant="body2" className={classes.secondaryText}>
                            {truncateText(tutor.nombre, 24)}
                          </Typography>
                          <Typography variant="caption" className={classes.metaText}>
                            CI {tutor.ci}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage=""
        labelDisplayedRows={({ from, to, count }) => `Mostrando ${from}-${to} de ${count}`}
      />
    </Paper>
  );
};

export default TablaPracticas;
